import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Sparkles, ArrowRight, AlertCircle, CheckCircle2, X } from 'lucide-react';

/**
 * Catch-Up Missions — student inbox.
 *
 * Renders a flat, severity-sorted list of open + in-progress missions.
 * The first card carries a slightly stronger "Start here →" affordance;
 * the rest are equally weighted. No tiering, no unlocks.
 */
export default function StudentMissions() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assessmentId = searchParams.get('assessment_id');
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null); // mission_id mid-flight
  const [skipping, setSkipping] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (assessmentId) params.assessment_id = assessmentId;
      const { data } = await api.get('/me/missions', { params });
      setMissions(data.missions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load missions');
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => { load(); }, [load]);

  const startMission = async (m) => {
    setStarting(m.mission_id);
    try {
      const { data } = await api.post(`/me/missions/${m.mission_id}/start`);
      // Resolve child_id from the mission (server returns conversation_id;
      // child_id is on the mission record).
      const childId = m.child_id;
      navigate(`/child/${childId}/chat/${data.conversation_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start mission');
    } finally {
      setStarting(null);
    }
  };

  const skipMission = async (m) => {
    if (!window.confirm(`Skip "${m.concept_tag}"? You can still revisit this topic later via the Socratic chat.`)) return;
    setSkipping(m.mission_id);
    try {
      await api.post(`/me/missions/${m.mission_id}/skip`);
      toast.success('Mission skipped');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to skip mission');
    } finally {
      setSkipping(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 pb-16"
      data-testid="student-missions-page"
    >
      <header className="border-b bg-white/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="missions-back-btn">
            ← Back
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-slate-900 flex items-center gap-2">
              <Sparkles size={18} className="text-amber-500" />
              Topics to improve
            </h1>
            <p className="text-xs text-slate-500">
              {missions.length === 0
                ? 'No catch-ups right now — nice work!'
                : `${missions.length} catch-up${missions.length === 1 ? '' : 's'} ready. Pick any one to start — no rush.`}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-3" data-testid="missions-list">
        {missions.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-10 text-center">
            <CheckCircle2 size={36} className="mx-auto text-emerald-400 mb-3" />
            <p className="text-slate-700 font-medium">You're all caught up.</p>
            <p className="text-sm text-slate-500 mt-1">
              New catch-ups will appear here after your next assessment.
            </p>
          </div>
        ) : (
          missions.map((m, idx) => (
            <MissionRow
              key={m.mission_id}
              mission={m}
              isFirst={idx === 0}
              starting={starting === m.mission_id}
              skipping={skipping === m.mission_id}
              onStart={() => startMission(m)}
              onSkip={() => skipMission(m)}
            />
          ))
        )}
      </main>
    </div>
  );
}

function MissionRow({ mission, isFirst, starting, skipping, onStart, onSkip }) {
  const m = mission;
  const tagSlug = (m.concept_tag || '').replace(/\s+/g, '-').toLowerCase();
  const isInProgress = m.status === 'in_progress';
  const triggerLabel = {
    wrong_choice: 'questions missed',
    weak_reasoning: 'reasoning to strengthen',
    mixed: 'gaps to close',
  }[m.trigger_type] || 'topic to revisit';

  return (
    <article
      className={`bg-white border rounded-xl p-4 flex items-start gap-3 hover:shadow-sm transition ${isFirst ? 'ring-1 ring-indigo-200' : ''}`}
      data-testid={`mission-row-${tagSlug}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <h2 className="font-semibold text-slate-900 truncate" title={m.concept_tag}>
            {m.concept_tag}
          </h2>
          {isInProgress && (
            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300">
              In progress
            </Badge>
          )}
        </div>
        <p className="text-xs text-slate-500">
          {m.source?.question_ids?.length || 1} {triggerLabel} · about 5 minutes
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          size="sm"
          variant="ghost"
          onClick={onSkip}
          disabled={starting || skipping}
          aria-label="Skip mission"
          className="text-slate-400 hover:text-rose-600"
          data-testid={`mission-skip-${tagSlug}`}
        >
          {skipping ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
        </Button>
        <Button
          size="sm"
          onClick={onStart}
          disabled={starting || skipping}
          data-testid={`mission-start-${tagSlug}`}
        >
          {starting ? (
            <><Loader2 size={14} className="animate-spin mr-1" /> Preparing…</>
          ) : (
            <>
              {isFirst ? 'Start here' : (isInProgress ? 'Continue' : 'Start')}
              <ArrowRight size={14} className="ml-1" />
            </>
          )}
        </Button>
      </div>
    </article>
  );
}
